import { useCallback, useRef } from 'react';

import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore, useRoomStore } from '@src/stores';

import { useRoomContext } from '@src/contexts';

import { ICanvasObject } from '@src/types';

import { generateUniqueId, getInterpolatedPoints, getObjectIdFromNode } from '../../helpers';

const useEraserTool = (
  currentCreatedById: string,
  emitDeleteAnnotation: (objects: ICanvasObject[]) => Promise<void>
) => {
  const erasingObjectsRef = useRef<Set<string>>(new Set());
  const lastEraserPosRef = useRef<{ x: number; y: number } | null>(null);

  const { currentLinePoints, eraserStrokes, objects, setCurrentLinePoints, setEraserStrokes, setObjects } =
    useMagicPenStore(
      useShallow((state) => ({
        currentLinePoints: state.currentLinePoints,
        eraserStrokes: state.eraserStrokes || [],
        objects: state.objects,
        setCurrentLinePoints: state.setCurrentLinePoints,
        setEraserStrokes: state.setEraserStrokes,
        setObjects: state.setObjects,
      }))
    );

  const { canAnnotate, isHost, isCurrentPeerSharing } = useRoomContext();

  const { hasMagicPenAnnotatePermission } = useRoomStore(
    useShallow((state) => ({
      hasMagicPenAnnotatePermission: state.hasMagicPenAnnotatePermission,
    }))
  );

  // Granted attendees (non-host, non-screen-sharer) can only delete their own objects
  const isGrantedNonHostNonSharer = hasMagicPenAnnotatePermission && !isHost && !isCurrentPeerSharing;
  const canDeleteOthersObjects = !isGrantedNonHostNonSharer && canAnnotate;

  const handleEraserLogic = useCallback(
    (stage: Konva.Stage, pos: { x: number; y: number }, isMouseUp = false) => {
      // HANDLE MOUSE UP
      if (isMouseUp) {
        // Save eraser stroke (visual feedback)
        if (currentLinePoints.length > 2) {
          const strokeId = generateUniqueId();
          setEraserStrokes([...eraserStrokes, { id: strokeId, points: currentLinePoints, createdAt: Date.now() }]);
        }

        // Filter objects that user is allowed to delete
        const allowedToDeleteIds = Array.from(erasingObjectsRef.current).filter((id) => {
          const obj = objects.find((o) => o.id === id);
          return obj?.createdBy?.peer_id === currentCreatedById || canDeleteOthersObjects;
        });

        if (allowedToDeleteIds.length > 0) {
          const deletionTime = Date.now();

          // Update local state with deletion mark
          setObjects(
            objects.map((obj) => (allowedToDeleteIds.includes(obj.id) ? { ...obj, deletedAt: deletionTime } : obj))
          );

          // Emit to backend
          emitDeleteAnnotation(objects.filter((obj) => allowedToDeleteIds.includes(obj.id)));
        }

        // Cleanup
        erasingObjectsRef.current.clear();
        lastEraserPosRef.current = null;
        setCurrentLinePoints([]);

        return;
      }

      // HANDLE MOUSE MOVE
      // Save points for eraser stroke
      setCurrentLinePoints([...currentLinePoints, pos.x, pos.y]);

      // Get points to check for intersections
      const checkPoints = getInterpolatedPoints(pos, lastEraserPosRef.current);

      // Update last point
      lastEraserPosRef.current = pos;

      // Detect collisions
      checkPoints.forEach((point) => {
        const node = stage.getIntersection(point);

        if (!node) return;

        const id = getObjectIdFromNode(node);
        if (!id || erasingObjectsRef.current.has(id)) return;

        const obj = objects.find((o) => o.id === id);
        if (!obj) return;

        // Granted attendees can only erase their own objects; others can erase if canDeleteOthersObjects
        if (obj.createdBy?.peer_id === currentCreatedById || canDeleteOthersObjects) {
          erasingObjectsRef.current.add(id);
        }
      });
    },
    [
      setCurrentLinePoints,
      currentLinePoints,
      setEraserStrokes,
      eraserStrokes,
      objects,
      currentCreatedById,
      canDeleteOthersObjects,
      setObjects,
      emitDeleteAnnotation,
    ]
  );

  const handleEraserClick = useCallback(
    (node: Konva.Node) => {
      const objectId = getObjectIdFromNode(node);

      if (!objectId) return;

      const targetObject = objects.find((obj) => obj.id === objectId);
      if (!targetObject) return;

      // Check if user is allowed to delete this object
      const canDeleteThis = targetObject.createdBy?.peer_id === currentCreatedById || canDeleteOthersObjects;
      if (!canDeleteThis) return;

      const deletionTime = Date.now();

      // Update local state with deletion mark to trigger fade animation
      setObjects(objects.map((obj) => (obj.id === objectId ? { ...obj, deletedAt: deletionTime } : obj)));

      // Emit to backend
      emitDeleteAnnotation([targetObject]);
    },

    [emitDeleteAnnotation, objects, setObjects, currentCreatedById, canDeleteOthersObjects]
  );

  // Expose refs for external use
  const getEraserRefs = () => ({
    erasingObjectsRef,
    lastEraserPosRef,
  });

  return {
    handleEraserLogic,
    handleEraserClick,
    getEraserRefs,
  };
};

export default useEraserTool;
